// @flow

import React, { useState } from "react";
import classNames from "classnames";
import { LabelTemplate } from "@components/schema-form/templates";
import type { WidgetProps } from "@rjsf/utils";
import Checkbox from "../../checkbox";
import Icon from "../../icon";

type Option = { value: string | number, label: string };

type Props = $Exact<{
  ...WidgetProps,
  options: Object, // label must be placed in uiSchema > "ui:options" > label
}>;

/**
 * Usage
 * In the schema you should put uniqueItems: true, otherwise will load the arrayTemplate instead of this widget
 *  schema example :
                      daysWeekly: {
                        type: "array",
                        uniqueItems: true,
                        items: {
                          enum: ["monday","tuesday","wednesday","thursday","friday","saturday","sunday",],
                        },
                      },
 uiSchema example :
                       daysWeekly: {
                        "ui:widget": "checkboxes",
                        "ui:options": {
                          label: "On which day do you want to receive the report?",
                          labelposition: "left",
                          required: true,
                          capitalizeLabel: true,
                        },
                      },

 Working example : /src/modules/reporting/components/schedule-reports-settings-modal/index.js
 */

export function CheckboxesWidget(props: Props) {
  const [selectedEnums, setSelectedEnums] = useState(props.value);

  const { options } = props;
  const { labelposition, label, labelclass, required, enumOptions } = options;

  function handleChange(id: string) {
    return function(isChecked: boolean) {
      let newArray = [];
      if (isChecked === "true") {
        newArray = [...selectedEnums, id];
      } else {
        newArray = selectedEnums.toSpliced(selectedEnums.indexOf(id), 1);
      }
      setSelectedEnums(_ => newArray);
      props.onChange(newArray);
    };
  }

  return (
    <div
      className={classNames("schema-form-widget-checkboxes justify-content-center d-flex", {
        "flex-column": !labelposition,
        "flex-row": labelposition === "left",
        "flex-row-reverse": labelposition === "right",
      })}
    >
      {/* LABEL */}
      {label && (
        <div
          className={classNames(
            "px-0 pt-1 form-field-label align-self-stretch justify-content-start d-flex flex-grow-1",
            labelclass,
          )}
        >
          <LabelTemplate className="d-flex align-self-baseline" {...props} />
        </div>
      )}

      {/* ASTERISK */}
      <div
        className={classNames(
          "text-warning font-size-20 align-self-stretch pt-1 pr-2 opacity-100",
          {
            "opacity-100": !props.disabled && props.required, // I'm using opacity so when I hide the elem. to not shrink the label or the input
            "opacity-0": props.disabled || !props.required,
          },
        )}
      >
        *
      </div>
      <div className="p-0 form-field-content align-self-baseline flex-grow-2">
        {/* CHECKBOXES */}
        {enumOptions.map((option: Option) => {
          const id = option.label;
          let lowercasedString = id.toLowerCase();
          let label = lowercasedString.charAt(0).toUpperCase() + lowercasedString.slice(1);

          return (
            <Checkbox
              key={id}
              id={id}
              name={id}
              label={label}
              className={`form-control ${props.className}`}
              checked={selectedEnums.indexOf(option.value) > -1}
              readOnly={props.readonly}
              disabled={props.disabled}
              autoFocus={props.autofocus}
              onChange={handleChange(id)}
            />
          );
        })}
      </div>
    </div>
  );
}

export default CheckboxesWidget;
